import React, { useEffect, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { NS, U2104ROLES } from "../../../../U2104";
import { FaRegFile } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import type { U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { emptyAvailableLogicalUnit, type U2104AvailableLogicalUnit } from "../../../../U2104Service";
import type { U2104OrganizationFormExtendedHandlers } from "../../../U2104OrganizationForm";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { u2104OrganizationAvailableLogicalUnitHandler } from "./U2104OrganizationAvailableLogicalUnitHandlers";
import U2104OrganizationAvailableLogicalUnitEditDlg from "./U2104OrganizationAvailableLogicalUnitEditDlg";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";

interface U2104OrganizationAvailableLogicalUnitBodyProps {
  u2104OrganizationFormHandlers: U2104OrganizationFormExtendedHandlers;
}

function getColumnsDef(t: TFunction, lang: string = "fr"): U2000TableColumn[] {
  return [
    {
      name: "logicalUnitCode",
      label: t("lblAvailableLogicalUnitCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[160px]",
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitDescription",
      label: t("lblAvailableLogicalUnitDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitCategoryLabelFr",
      label: t("lblLogicalUnitCategory", { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      widthClass: "w-[180px]",
      type: "string",
      align: "left",
      display: (_column, _value, row: any) => {
        if (!row?.logicalUnitCategoryId) {
          return t("lblNoLogicalUnitCategory", { ns: NS });
        }
        const label = lang.startsWith("en")
          ? row.logicalUnitCategoryLabelEn || row.logicalUnitCategoryLabelFr
          : row.logicalUnitCategoryLabelFr || row.logicalUnitCategoryLabelEn;
        return label || row.logicalUnitCategoryCode || "";
      },
    },
  ];
}

const U2104OrganizationAvailableLogicalUnitBody: React.FC<U2104OrganizationAvailableLogicalUnitBodyProps> = ({
  u2104OrganizationFormHandlers: u2104OrganizationFormSHandlers,
}) => {
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const gridStateHandlers = useU2000GridStateHandlers<U2104AvailableLogicalUnit>();

  const validateAvailableLogicalUnit = (item: U2104AvailableLogicalUnit) => {
    const result: Record<string, string> = {};
    if (!item.logicalUnitId) {
      result.logicalUnitId = t("errAvailableLogicalUnitRequired", { ns: NS });
    }
    const duplicate = gridStateHandlers
      .getData()
      .find((row) => row.logicalUnitId === item.logicalUnitId && row.id !== item.id);
    if (duplicate) {
      result.logicalUnitId = t("errAvailableLogicalUnitDuplicate", { ns: NS });
    }
    return result;
  };

  const businessHandlers: U2000FormSBusinessHandlers<U2104AvailableLogicalUnit> = {
    newRecord: () => emptyAvailableLogicalUnit,
    validateForm: validateAvailableLogicalUnit,
  };

  const formStateHandlers = useU2000FormStateHandlers<U2104AvailableLogicalUnit>({
    formState,
    formStateDispatch,
    businessHandlers,
  });

  const availableLuHandlers = u2104OrganizationAvailableLogicalUnitHandler({
    u2000AxiosAuthenticated,
    gridStateHandlers,
    t,
    formStateHandlers,
    getOrganization: () => u2104OrganizationFormSHandlers.getData?.(),
  });

  useEffect(() => {
    const columns = getColumnsDef(t, i18n.language);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
    const availableLogicalUnits = u2104OrganizationFormSHandlers.getData?.()?.availableLogicalUnits;
    const rows = Array.isArray(availableLogicalUnits) ? availableLogicalUnits : [];
    gridStateHandlers.setData(rows);
    gridStateHandlers.setTotalRows(rows.length);
  }, [u2104OrganizationFormSHandlers, t, i18n.language]);

  useEffect(() => {
    if (availableLuHandlers.dataChangedEvent === 0) return;
    const org = u2104OrganizationFormSHandlers.getData?.();
    u2104OrganizationFormSHandlers.setData({
      ...org,
      availableLogicalUnits: gridStateHandlers.getData(),
    });
  }, [availableLuHandlers.dataChangedEvent]);

  const actions = React.useMemo(
    () => [
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: availableLuHandlers.onAdd,
        hasRole: hasRole(U2104ROLES.ROLES),
        disabled: u2104OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.READ,
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
    ],
    [t, availableLuHandlers, hasRole, u2104OrganizationFormSHandlers],
  );

  return (
    <>
      <U2104OrganizationAvailableLogicalUnitEditDlg handler={availableLuHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={false}
        label={t("lblAvailableLogicalUnit", {
          count: availableLuHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U2104OrganizationAvailableLogicalUnitBody;
